import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import {
  ArrowRightSmallIcon,
  CheckIcon,
  EnvelopeIcon,
  SocialsIcon,
  UserPlusIcon,
  VectorRightCurved,
} from '@/assets/icons'
import { useSupportLinks } from '@/components/support-links'
import type { Translations } from '@/localise'

type FreeBankrollEntriesSectionProps = {
  isAuthorized: boolean
}

export const FreeBankrollEntriesSection = ({ isAuthorized }: FreeBankrollEntriesSectionProps) => {
  const supportLinks = useSupportLinks()
  const discordSupportLink = supportLinks.find((link) => link.gaTag === 'discord')
  const telegramSupportLink = supportLinks.find((link) => link.gaTag === 'telegram')

  return (
    <section className="flex flex-col items-center py-[52px] md:py-[40px]">
      <Typography
        className="text-center align-middle"
        component="h2"
        variant="h4"
      >
        <FormattedMessage id="freeBankroll.steps.title" />
      </Typography>

      <div className="mt-[64px] flex w-full flex-row gap-6 md:mt-[52px] md:flex-col md:gap-10">
        <EntryCard
          completeWhenAuthorized
          buttonLabel="freeBankroll.steps.createAccount"
          descriptionId="freeBankroll.action.createAccount.description"
          entriesId="freeBankroll.steps.createAccount.entries"
          href="/login"
          isAuthorized={isAuthorized}
          icon={
            <UserPlusIcon
              height={32}
              width={32}
            />
          }
        />
        <EntryCard
          buttonLabel="freeBankroll.steps.discord"
          descriptionId="freeBankroll.action.discord.description"
          disabled={!discordSupportLink}
          entriesId="freeBankroll.steps.discord.entries"
          href={discordSupportLink?.href}
          target={discordSupportLink ? '_blank' : undefined}
          icon={
            <SocialsIcon
              height={32}
              width={32}
            />
          }
        />
        <EntryCard
          buttonLabel="freeBankroll.steps.message"
          descriptionId="freeBankroll.action.message.description.plain"
          disabled={!telegramSupportLink}
          entriesId="freeBankroll.steps.message.entries"
          href={telegramSupportLink?.href}
          target={telegramSupportLink ? '_blank' : undefined}
          icon={
            <EnvelopeIcon
              height={32}
              width={32}
            />
          }
        />
      </div>
    </section>
  )
}

type EntryCardProps = {
  buttonLabel: keyof Translations
  descriptionId: keyof Translations
  entriesId: keyof Translations
  icon: ReactNode
  isAuthorized?: boolean
  completeWhenAuthorized?: boolean
  href?: string
  target?: '_blank'
  disabled?: boolean
}

const EntryCard = ({
  buttonLabel,
  descriptionId,
  entriesId,
  icon,
  isAuthorized = false,
  completeWhenAuthorized = false,
  href,
  target,
  disabled = false,
}: EntryCardProps) => {
  return (
    <Paper
      className="relative flex-1 rounded-md border border-divider px-12 pb-8 pt-12 [&:last-child_.vector-right-curved]:hidden [&:nth-of-type(even)_.vector-right-curved]:translate-x-1/2 [&:nth-of-type(even)_.vector-right-curved]:rotate-180 [&:nth-of-type(even)_.vector-right-curved]:-scale-x-100 md:[&:nth-of-type(even)_.vector-right-curved]:left-0 md:[&:nth-of-type(even)_.vector-right-curved]:-translate-y-1/2 md:[&:nth-of-type(even)_.vector-right-curved]:translate-x-0 md:[&:nth-of-type(even)_.vector-right-curved]:-rotate-90 sm:[&:nth-of-type(even)_.vector-right-curved]:-translate-x-[30%]"
      elevation="1"
    >
      <TopButton
        completeWhenAuthorized={completeWhenAuthorized}
        disabled={disabled}
        href={href}
        isAuthorized={isAuthorized}
        label={buttonLabel}
        target={target}
      />

      <div className="vector-right-curved absolute left-full top-[40%] z-[1] -translate-x-1/2 -translate-y-1/2 md:top-full md:-translate-x-full md:-translate-y-1/2 md:rotate-90 sm:-translate-x-[70%]">
        <VectorRightCurved />
      </div>

      <div
        className="flex flex-col items-center gap-4"
        style={{ minHeight: 152 }}
      >
        <div className="text-white">{icon}</div>
        <Typography
          className="text-center"
          color="text.primary"
          style={{ maxWidth: '220px' }}
          variant="body2"
        >
          <FormattedMessage id={descriptionId} />
        </Typography>
        <Typography
          color="primary"
          variant="h6"
        >
          <FormattedMessage id={entriesId} />
        </Typography>
      </div>
    </Paper>
  )
}

type TopButtonProps = {
  label: keyof Translations
  isAuthorized?: boolean
  completeWhenAuthorized?: boolean
  href?: string
  target?: '_blank'
  disabled?: boolean
}

const TopButton = ({
  label,
  isAuthorized = false,
  completeWhenAuthorized = false,
  href,
  target,
  disabled,
}: TopButtonProps) => {
  const isCreateAccountLabel = label === 'freeBankroll.steps.createAccount'
  const createAccountEndIcon = isCreateAccountLabel ? (
    <div className="inline-flex size-6">
      <ArrowRightSmallIcon
        height={24}
        width={24}
      />
    </div>
  ) : undefined

  if (isAuthorized && completeWhenAuthorized) {
    return (
      <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-6">
        <Button
          className="bg-[#313133] text-primary-main shadow-[0px_4px_24px_0px_#FFAB0057] hover:bg-[#313133]"
          size="large"
          variant="contained"
          endIcon={
            <div className="inline-flex size-6">
              <CheckIcon
                height={24}
                width={24}
              />
            </div>
          }
        >
          <FormattedMessage id="freeBankroll.steps.complete" />
        </Button>
      </div>
    )
  }

  return (
    <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-6 before:absolute before:inset-0 before:bg-warning-main before:opacity-50 before:blur-[10px] before:content-['']">
      {target === '_blank' ? (
        <Button
          as="a"
          className="relative min-w-[150px]"
          color="warning"
          disabled={disabled}
          endIcon={createAccountEndIcon}
          href={href}
          rel="noopener noreferrer"
          size="large"
          target="_blank"
          variant="contained"
        >
          <FormattedMessage id={label} />
        </Button>
      ) : (
        <Button
          as={Link}
          className="relative min-w-[150px]"
          color="warning"
          disabled={disabled}
          endIcon={createAccountEndIcon}
          preload={false}
          size="large"
          to={href ?? '/login'}
          variant="contained"
        >
          <FormattedMessage id={label} />
        </Button>
      )}
    </div>
  )
}
